/* eslint-disable max-lines */
import { dialogDefaultState, INTERVIEW_MODE, STEP, ZERO } from '@constants';
import {
  CommentType,
  InterviewData,
  Supplier,
  Nullable,
  AttributeDetails,
  ReviewFormsState,
  FormStatePayload,
  GeneralAnswers,
} from '@customTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AttributeExcludeDialog,
  CommentActions,
  CommentsModalState,
  FormState,
  InterviewDetailsDialog,
  SelectedInterview,
  SupplierDeleteDialog,
  SupplierScoreDialog,
  UpdatedSupplier,
} from '@store/types';

export interface InterviewState {
  selectedInterview: Nullable<SelectedInterview>;
  activeTab: number;
  savedTabs: number[];
  isInterviewMode: boolean;
  selectedInterviewData: Nullable<InterviewData>;
  commentsModal: CommentsModalState;
  comments: CommentType[];
  suppliers: Supplier[];
  initialSuppliers: Supplier[];
  currentSuppliers: Supplier[];
  formState: FormState;
  attributes: AttributeDetails[];
  isInfoDialogOpen: boolean;
  isExcludedDialogOpen: boolean;
  isInterviewCompleted: boolean;
  generalQuestions: GeneralAnswers;
  supplierScoreDialog: SupplierScoreDialog;
  isInterviewReview: boolean;
  updatedSupplier: Nullable<UpdatedSupplier>;
  supplierDeleteDialog: SupplierDeleteDialog;
  attributeExcludeDialog: AttributeExcludeDialog;
  isExitInterviewOpen: boolean;
  reviewFormsState: ReviewFormsState;
  emptyTabs: number[];
  interviewDetailsImportDialog: InterviewDetailsDialog;
  interviewViewMode: INTERVIEW_MODE;
}

const initialState: InterviewState = {
  selectedInterview: null,
  selectedInterviewData: null,
  activeTab: ZERO,
  savedTabs: [],
  isInterviewMode: false,
  commentsModal: dialogDefaultState,
  comments: [],
  suppliers: [],
  initialSuppliers: [],
  currentSuppliers: [],
  attributes: [],
  isInfoDialogOpen: false,
  isExcludedDialogOpen: false,
  isInterviewCompleted: false,
  generalQuestions: {},
  supplierScoreDialog: dialogDefaultState,
  isInterviewReview: false,
  updatedSupplier: null,
  supplierDeleteDialog: dialogDefaultState,
  attributeExcludeDialog: dialogDefaultState,
  formState: {
    isDirty: false,
    isValid: true,
  },
  isExitInterviewOpen: false,
  reviewFormsState: {
    SUPPLIERS: true,
    GENERAL_QUESTIONS: true,
    PRICE_SENSITIVITY: true,
    RELATIVE_PRICES: true,
    ATTRIBUTE_PERFORMANCE: true,
  },
  emptyTabs: [],
  interviewDetailsImportDialog: dialogDefaultState,
  interviewViewMode: INTERVIEW_MODE.WIZARD,
};

export const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    setSelectedInterview: (state, action: PayloadAction<Nullable<SelectedInterview>>) => {
      state.selectedInterview = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload;
    },
    setSavedTabs: (state, action: PayloadAction<number[]>) => {
      state.savedTabs = action.payload;
    },
    handleMoveNextTab: (state) => {
      if (!state.savedTabs.includes(state.activeTab)) {
        state.savedTabs = [state.activeTab, ...state.savedTabs];
      }

      state.activeTab += STEP;
    },
    setInterviewMode: (state, action: PayloadAction<boolean>) => {
      state.isInterviewMode = action.payload;
    },
    setSelectedInterviewData: (state, action: PayloadAction<Nullable<InterviewData>>) => {
      state.selectedInterviewData = action.payload;
    },
    setCommentsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.commentsModal.isOpen = action.payload;
    },
    setCommentsAction: (state, action: PayloadAction<CommentActions>) => {
      state.commentsModal = { ...action.payload, isOpen: state.commentsModal.isOpen };
    },
    setComments: (state, action: PayloadAction<CommentType[]>) => {
      state.comments = action.payload;
    },
    setSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.suppliers = action.payload;
    },
    setInitialSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.initialSuppliers = action.payload;
    },
    setCurrentSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.currentSuppliers = action.payload;
    },
    setFormState: (state, action: PayloadAction<FormState>) => {
      state.formState = action.payload;
    },
    setAttributes: (state, action: PayloadAction<AttributeDetails[]>) => {
      state.attributes = action.payload;
    },
    toggleInfoDialog: (state) => {
      state.isInfoDialogOpen = !state.isInfoDialogOpen;
    },
    toggleExcludedDialog: (state) => {
      state.isExcludedDialogOpen = !state.isExcludedDialogOpen;
    },
    setInterviewCompleted: (state, action: PayloadAction<boolean>) => {
      state.isInterviewCompleted = action.payload;
    },
    setGeneralQuestions: (state, action: PayloadAction<GeneralAnswers>) => {
      state.generalQuestions = action.payload;
    },
    setSupplierScoreDialog: (state, action: PayloadAction<SupplierScoreDialog>) => {
      state.supplierScoreDialog = action.payload;
    },
    setInterviewReview: (state, action: PayloadAction<boolean>) => {
      state.isInterviewReview = action.payload;
    },
    setUpdatedSupplier: (state, action: PayloadAction<Nullable<UpdatedSupplier>>) => {
      state.updatedSupplier = action.payload;
    },
    setSupplierDeleteDialog: (state, action: PayloadAction<SupplierDeleteDialog>) => {
      state.supplierDeleteDialog = action.payload;
    },
    setAttributeExcludeDialog: (state, action: PayloadAction<AttributeExcludeDialog>) => {
      state.attributeExcludeDialog = action.payload;
    },
    setExitInterviewOpen: (state, action: PayloadAction<boolean>) => {
      state.isExitInterviewOpen = action.payload;
    },
    resetInterviewState: (state) => {
      state.commentsModal = initialState.commentsModal;
      state.comments = initialState.comments;
      state.suppliers = initialState.suppliers;
      state.initialSuppliers = initialState.initialSuppliers;
      state.currentSuppliers = initialState.currentSuppliers;
      state.attributes = initialState.attributes;
      state.generalQuestions = initialState.generalQuestions;
      state.isInterviewReview = initialState.isInterviewReview;
      state.updatedSupplier = initialState.updatedSupplier;
      state.formState = initialState.formState;
      state.activeTab = initialState.activeTab;
      state.savedTabs = initialState.savedTabs;
      state.emptyTabs = initialState.emptyTabs;
      state.attributeExcludeDialog = initialState.attributeExcludeDialog;
    },
    resetAllDialogsState: (state) => {
      state.commentsModal = initialState.commentsModal;
      state.isInfoDialogOpen = initialState.isInfoDialogOpen;
      state.isExcludedDialogOpen = initialState.isExcludedDialogOpen;
      state.supplierScoreDialog = initialState.supplierScoreDialog;
      state.supplierDeleteDialog = initialState.supplierDeleteDialog;
      state.attributeExcludeDialog = initialState.attributeExcludeDialog;
      state.isExitInterviewOpen = initialState.isExitInterviewOpen;
      state.interviewDetailsImportDialog = initialState.interviewDetailsImportDialog;
    },
    setReviewFormsState: (state, action: PayloadAction<FormStatePayload>) => {
      state.reviewFormsState[action.payload.key] = action.payload.value;
    },
    resetSelectedInterview: (state) => {
      state.selectedInterview = initialState.selectedInterview;
      state.selectedInterviewData = initialState.selectedInterviewData;
    },
    setEmptyTab: (state, action: PayloadAction<number>) => {
      state.emptyTabs = [...state.emptyTabs, action.payload];
    },
    setInterviewDetailsImportDialog: (state, action: PayloadAction<InterviewDetailsDialog>) => {
      state.interviewDetailsImportDialog = action.payload;
    },
    setInterviewViewMode: (state, action: PayloadAction<INTERVIEW_MODE>) => {
      state.interviewViewMode = action.payload;
    },
  },
});

export const {
  setSelectedInterview,
  setActiveTab,
  setSavedTabs,
  setInterviewMode,
  setSelectedInterviewData,
  setComments,
  setCommentsModalOpen,
  setCommentsAction,
  setSuppliers,
  handleMoveNextTab,
  setFormState,
  setAttributes,
  toggleInfoDialog,
  toggleExcludedDialog,
  setInterviewCompleted,
  setGeneralQuestions,
  setSupplierScoreDialog,
  setInterviewReview,
  setUpdatedSupplier,
  setSupplierDeleteDialog,
  setAttributeExcludeDialog,
  setExitInterviewOpen,
  setInitialSuppliers,
  setCurrentSuppliers,
  resetInterviewState,
  resetAllDialogsState,
  setReviewFormsState,
  resetSelectedInterview,
  setEmptyTab,
  setInterviewDetailsImportDialog,
  setInterviewViewMode,
} = interviewSlice.actions;
export default interviewSlice.reducer;
